import { Box, GridJustification } from '@material-ui/core'
import {
  LineSeries,
  PointOrSliceMouseHandler,
  ResponsiveLineCanvas,
} from '@nivo/line'
import { isNil } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FullFormatOptions } from '../../../util/formatters'
import { ClipboardButton } from '../../system-container/charts/ClipboardButton'
import { Legends } from './Legends'
import { Tooltip } from './Tooltip'

const COLOR_SCHEME = [
  '#9dd354',
  '#dec55b',
  '#c0d4fd',
  '#a4a4a6',
  '#608D24',
  '#9C851F',
]

type Props = {
  data: LineSeries[]
  xFormatter: (time: number) => string
  yFormatter: (value: number, options?: FullFormatOptions) => string
  xAxisFormatter: (value: number) => string
  onClick?: PointOrSliceMouseHandler<LineSeries>
  alignLegends?: GridJustification
}

export function LineChart({
  data: series,
  xFormatter,
  yFormatter,
  xAxisFormatter,
  onClick,
  alignLegends,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const mapLegendData = (datum: LineSeries, index: number) => ({
    label: datum.id.toString(),
    color: COLOR_SCHEME[index % COLOR_SCHEME.length],
  })

  const referenceSerie = series[0].data
  const referenceLength = referenceSerie.length
  const nonmatchingXAxisData = [...series]
    .filter((_, index) => index !== 0)
    .some(
      ({ data }) =>
        referenceLength !== data.length ||
        data.some((point, index) => point.x !== referenceSerie[index].x),
    )

  const getClipboardText = () => {
    const headers = series.map(({ id }) => `${id}`)
    const separator = '\t'
    const headerRow = ['', ...headers].join(separator)

    const mapSerieToValue = (serie: LineSeries, index: number) =>
      Number(serie.data[index].y ?? 0)

    const dataRows = referenceSerie.map((datum, index) => {
      const firstCell = isNil(datum.x) ? '' : xFormatter(Number(datum.x))
      const values = series.map((serie) => mapSerieToValue(serie, index))
      return [firstCell, ...values].join(separator)
    })

    return [headerRow, ...dataRows].join('\n')
  }

  return (
    <>
      <Box display="flex" height="85%" justifyContent="flex-end">
        <ClipboardButton
          tooltip={
            nonmatchingXAxisData
              ? t('charts.copyToClipboardDisabled')
              : t('charts.copyToClipboard')
          }
          getText={getClipboardText}
          disabled={nonmatchingXAxisData}
        />
        <Box width="100%">
          <ResponsiveLineCanvas
            data={series}
            isInteractive
            enableGridX={false}
            margin={{ top: 8, right: 48, bottom: 80, left: 80 }}
            enablePoints={false}
            colors={series.map(
              (_, index) => COLOR_SCHEME[index % COLOR_SCHEME.length],
            )}
            tooltip={(props) => Tooltip({ ...props, xFormatter, yFormatter })}
            onClick={onClick}
            axisLeft={{
              format: yFormatter,
            }}
            axisBottom={{
              tickSize: 0,
              tickPadding: 16,
              tickRotation: 30,
              format: xAxisFormatter,
            }}
            xScale={{
              type: 'linear',
              min: 'auto',
              max: 'auto',
              nice: false,
            }}
          />
        </Box>
      </Box>

      <Legends
        legendData={series.map(mapLegendData)}
        alignLegends={alignLegends}
      />
    </>
  )
}
